import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useParams } from 'react-router-dom';
import { axiosInstance } from 'setup/axiosInstance';
import {
  DictionaryIrregularVerb,
  LearningSession,
  VocabularyIrregularVerb,
} from 'app/types';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import FilledActionButton from 'components/FilledActionButton';
import PageIntro from 'components/PageIntro/PageIntro';
import UIContainer from 'components/ui/UIContainer';
import WithLoader from 'components/WithLoader';
import { CEFR_META } from 'components/cefrMeta';
import { NotificationCenterContext } from 'components/NotificationCenter';
import { fillUrlIds, pagePaths } from 'routes/paths';
import VocabularyVerbsView, {
  STATUS_FILTER_CHIPS,
  statusColor,
  useVocabularyVerbsView,
  type WordStatus,
} from 'components/vocabularyVerbsView';
import StatusFilterChips from 'components/StatusFilterChips';
import { useTheme } from '@mui/material/styles';
import TableSettingsDialog from 'components/TableSettingsDialog';
import {
  createDefaultColumnSettings,
  tableSettingsToColumnsVisibility,
} from 'components/tableSettings';
import { useTableSettings } from 'hooks/useTableSettings';
import {
  FormType,
  getFormDescription,
  getPatternLabel,
} from './patternSuperGroups';

/**
 * Dedicated page for "open the drill of one irregular-verb category".
 *
 *   axis = "pattern"  → /wordset/irregular-verb/pattern/<patternKey>
 *   axis = "form"     → /wordset/irregular-verb/form/<AAA|ABB|ABA|ABC>
 *   axis = "family"   → /wordset/irregular-verb/family/<baseInfinitive>
 *   axis = "level"    → /wordset/irregular-verb/level/<A1..C2>
 *
 * Layout matches the word-set sub-category page
 * (`/wordset/level-based/A1/<sub>`) — same PageIntro (title, level pill,
 * counts, progress bar) with "Почати вправи" + "Додати в словник" in the
 * breadcrumb row, same FilterBar shape (search + status chips + view toggle
 * + "Сховати відомі" + settings gear), same alphabetical grouping (no
 * group-by picker — "Абетка" is the only mode that makes sense here).
 */
type Axis = 'pattern' | 'form' | 'family' | 'level';

interface Props {
  axis: Axis;
}

const ENDPOINT_BY_AXIS: Record<Axis, string> = {
  pattern: '/wordset/irregular-verb/pattern',
  form: '/wordset/irregular-verb/form',
  family: '/wordset/irregular-verb/family',
  level: '/wordset/irregular-verb',
};

const VALID_FORMS: ReadonlySet<string> = new Set<string>(['AAA', 'ABB', 'ABA', 'ABC']);

const titleForAxis = (
  axis: Axis,
  value: string,
): { title: string; levelCode?: string; levelName?: string; subtitle?: string } => {
  switch (axis) {
    case 'pattern':
      return { title: getPatternLabel(value), subtitle: value };
    case 'form':
      return {
        title: `Форма ${value}`,
        subtitle: VALID_FORMS.has(value) ? getFormDescription(value as FormType) : '',
      };
    case 'family':
      return { title: `Сімейство ${value}`, subtitle: `Усі похідні від «${value}»` };
    case 'level': {
      const cefr = CEFR_META[value];
      return {
        title: cefr?.en ?? `Рівень ${value}`,
        levelCode: value,
        levelName: cefr?.en,
      };
    }
  }
};

const DEFAULT_VOCABULARY_VERBS_COLUMNS = createDefaultColumnSettings({
  number: true,
  infinitive: true,
  infinitiveTranscription: true,
  pastSimple: true,
  pastSimpleTranscription: true,
  pastParticiple: true,
  pastParticipleTranscription: true,
  translation: true,
});

type VerbAddStatus = 'notLearned' | 'learned' | 'repeat';
const verbAddStatusOptions: Array<{
  label: string;
  value: VerbAddStatus;
  isActiveSpeech: boolean;
  isLearned: boolean;
}> = [
  { label: 'Не вивчено', value: 'notLearned', isLearned: false, isActiveSpeech: false },
  { label: 'Вивчено', value: 'learned', isLearned: true, isActiveSpeech: false },
  { label: 'Потребує повторення', value: 'repeat', isLearned: true, isActiveSpeech: false },
];

const IrregularVerbCategoryPage: React.FC<Props> = ({ axis }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pushNotification } = useContext(NotificationCenterContext);
  const params = useParams();
  const rawValue =
    axis === 'pattern'
      ? params.patternName
      : axis === 'form'
      ? params.formType
      : axis === 'family'
      ? params.baseVerb
      : params.level;
  const value = useMemo(() => {
    try {
      return rawValue ? decodeURIComponent(rawValue) : '';
    } catch {
      return rawValue ?? '';
    }
  }, [rawValue]);

  const [loading, setLoading] = useState(true);
  const [verbs, setVerbs] = useState<DictionaryIrregularVerb[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchVerbs = useCallback(() => {
    if (!value) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    axiosInstance
      .get<DictionaryIrregularVerb[]>(`${ENDPOINT_BY_AXIS[axis]}/${encodeURIComponent(value)}`)
      .then((res) => {
        if (cancelled) return;
        setVerbs(res.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message ?? 'fetch failed');
        setVerbs([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [axis, value]);

  useEffect(() => {
    fetchVerbs();
  }, [fetchVerbs]);

  // Cast: BE returns DictionaryIrregularVerbTO whose shape is a strict
  // superset of VocabularyIrregularVerb (everything the hook reads).
  const verbsForView = useMemo(
    () => verbs as unknown as VocabularyIrregularVerb[],
    [verbs],
  );

  const verbsView = useVocabularyVerbsView({ verbs: verbsForView, resetKey: 0 });

  // Force alphabetical grouping — group-by picker has no place on a category
  // drill (the user already filtered by category; further by-recent grouping
  // would be noise). We push this once on mount and on every fetch.
  useEffect(() => {
    if (verbsView.groupBy !== 'letter') {
      verbsView.setGroupBy('letter');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verbsView.groupBy]);

  // Dictionary view — table-level Status popover stays read-only via no-op.
  const noopSetStatus = useCallback((_id: number, _status: WordStatus) => {}, []);
  const noopDeleteOne = useCallback((_id: number) => {}, []);

  // ── Table settings (column visibility + page size + popover)
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
  const {
    resetSettings: resetTableSettings,
    saveSettings: saveTableSettings,
    settings: tableSettings,
  } = useTableSettings('DICTIONARY_IRREGULAR_VERBS', DEFAULT_VOCABULARY_VERBS_COLUMNS);
  // tableSettingsToColumnsVisibility is consumed by legacy WordsTable; the new
  // VocabularyVerbsView has its own column set. We keep the hook for the
  // settings popover only.
  void tableSettingsToColumnsVisibility(tableSettings, { select: true });

  // ── Start learning + Add to vocabulary actions
  const [learningLoading, setLearningLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addStatus, setAddStatus] = useState<VerbAddStatus>('notLearned');

  const startLearning = useCallback(async () => {
    const ids = verbsView.selectedIds;
    if (!ids.length) return;
    setLearningLoading(true);
    try {
      const { data: session } = await axiosInstance.post<LearningSession>(
        '/learning-sessions',
        {
          mode: 'MANUAL',
          source: 'DICTIONARY',
          irregularVerbIds: ids,
        },
      );
      navigate(
        fillUrlIds(pagePaths.learningSession, {
          learningSessionId: String(session.id),
        }),
      );
    } finally {
      setLearningLoading(false);
    }
  }, [navigate, verbsView.selectedIds]);

  const openAddDialog = () => {
    if (!verbsView.selectedIds.length) return;
    setAddStatus('notLearned');
    setAddDialogOpen(true);
  };
  const closeAddDialog = () => setAddDialogOpen(false);

  const addVerbsToVocab = useCallback(async () => {
    const selectedOption = verbAddStatusOptions.find((s) => s.value === addStatus);
    if (!selectedOption) return;
    const selectedSet = new Set(verbsView.selectedIds);
    const payload = verbs
      .filter((v) => selectedSet.has(v.id))
      .map((v) => ({
        ...v,
        isLearned: selectedOption.isLearned,
        isActiveSpeech: selectedOption.isActiveSpeech,
      }));
    if (!payload.length) return;
    setAdding(true);
    try {
      await axiosInstance.post(`/vocabulary/verbs/`, payload);
      setAddDialogOpen(false);
      pushNotification(
        `Додано ${payload.length} дієсл${payload.length === 1 ? 'ово' : 'ів'} у словник`,
        'success',
        undefined,
        'center',
      );
      verbsView.clear();
      // Re-fetch so newly-added verbs reflect the right counts /
      // statuses on this page without forcing a route refresh.
      fetchVerbs();
    } catch (e) {
      console.error(e);
    } finally {
      setAdding(false);
    }
  }, [addStatus, fetchVerbs, pushNotification, verbs, verbsView]);

  const heading = titleForAxis(axis, value);

  const total = verbs.length;
  const inVocab = useMemo(
    () => verbs.filter((v) => v.isLearned || v.isActiveSpeech).length,
    [verbs],
  );
  const newCount = Math.max(total - inVocab, 0);
  const pct = total ? Math.round((inVocab / total) * 100) : 0;
  const fmt = (n: number) => n.toLocaleString('uk-UA');
  const selectedCount = verbsView.selectedIds.length;
  const suffix = selectedCount ? ` · ${selectedCount}` : '';
  const actionsDisabled = !selectedCount;

  const pageActions = (
    <>
      <FilledActionButton
        disabled={actionsDisabled || learningLoading}
        onClick={startLearning}
        startIcon={<AutoStoriesOutlinedIcon sx={{ fontSize: 17 }} />}
      >
        {`Почати вправи${suffix}`}
      </FilledActionButton>
      <FilledActionButton
        disabled={actionsDisabled}
        loading={adding}
        onClick={openAddDialog}
        startIcon={<AddIcon sx={{ fontSize: 18 }} />}
      >
        {`Додати в словник${suffix}`}
      </FilledActionButton>
    </>
  );

  const levelChip = heading.levelCode ? (
    <Box
      sx={{
        bgcolor: (t) =>
          t.palette.mode === 'light'
            ? 'rgba(59,44,147,0.10)'
            : 'rgba(155,124,255,0.18)',
        borderRadius: 999,
        color: 'primary.main',
        display: 'inline-block',
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '0.04em',
        px: 1.375,
        py: '4px',
      }}
    >
      {heading.levelName
        ? `${heading.levelCode} · ${heading.levelName}`
        : heading.levelCode}
    </Box>
  ) : undefined;

  const introDescription = (
    <>
      {`${fmt(total)} слів · `}
      <Box component={'b'} sx={{ color: 'text.primary', fontWeight: 700 }}>
        {fmt(inVocab)}
      </Box>
      {' у твоєму словнику · '}
      <Box component={'b'} sx={{ color: 'primary.main', fontWeight: 700 }}>
        {fmt(newCount)}
      </Box>
      {' нових'}
    </>
  );

  const progressFooter = (
    <Stack direction={'row'} alignItems={'center'} spacing={1.75}>
      <Box
        sx={(t) => ({
          bgcolor:
            t.palette.mode === 'light' ? 'grey.200' : 'rgba(255,255,255,0.08)',
          borderRadius: 999,
          flex: 'none',
          height: 8,
          maxWidth: '60vw',
          overflow: 'hidden',
          width: 220,
        })}
      >
        <Box
          sx={(t) => ({
            bgcolor: t.palette.mode === 'light' ? '#3B8E2F' : '#5FC74C',
            height: '100%',
            transition: 'width .25s ease',
            width: `${pct}%`,
          })}
        />
      </Box>
      <Typography
        sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 700 }}
      >
        {`${pct}% у словнику`}
      </Typography>
    </Stack>
  );

  return (
    <>
      <UIContainer>
        <BreadCrumbs
          extraItems={[{ label: heading.title }]}
          actions={pageActions}
        />
      </UIContainer>

      <WithLoader loading={loading}>
        <UIContainer sx={{ pb: { xs: 4, md: 8 } }}>
          <PageIntro
            title={heading.title}
            description={introDescription}
            eyebrow={levelChip}
            footer={progressFooter}
          />

          {error ? (
            <Box sx={{ color: 'error.main', mb: 2 }}>{`Помилка: ${error}`}</Box>
          ) : null}

          {/* FilterBar row — mirrors DictionaryFilterBar from the words page:
              search · status chips · spacer · view toggle · Сховати відомі · settings */}
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={1.5}
            sx={{ flexWrap: 'wrap', rowGap: 1.5, mb: 2 }}
          >
            <TextField
              size={'small'}
              value={verbsView.search}
              onChange={(e) => verbsView.setSearch(e.target.value)}
              placeholder={'Пошук слова або перекладу…'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position={'start'}>
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 19 }} />
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: 'background.paper',
                  borderRadius: 999,
                  fontSize: 14,
                  height: 40,
                  '& fieldset': { borderRadius: 999 },
                },
              }}
              sx={{ flex: '1 1 220px', maxWidth: 320 }}
            />

            <StatusFilterChips
              value={verbsView.statusFilter}
              options={STATUS_FILTER_CHIPS.map((c) => ({
                value: c.value,
                label: c.label,
                color: c.status
                  ? statusColor(c.status as WordStatus, theme.palette.mode)
                  : undefined,
              }))}
              onChange={(next) => verbsView.setStatusFilter(next)}
            />

            <Box sx={{ flex: 1 }} />

            <Box
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'primary.tableBorder',
                borderRadius: 999,
                display: 'inline-flex',
                gap: '2px',
                p: '3px',
              }}
            >
              {(
                [
                  ['table', 'Список', <ViewListIcon key={'l'} sx={{ fontSize: 18 }} />],
                  ['cards', 'Плитка', <GridViewIcon key={'g'} sx={{ fontSize: 18 }} />],
                ] as const
              ).map(([mode, label, icon]) => {
                const on = verbsView.viewMode === mode;
                return (
                  <Box
                    key={mode}
                    component={'button'}
                    onClick={() => verbsView.setViewMode(mode)}
                    title={label}
                    sx={{
                      alignItems: 'center',
                      bgcolor: on ? 'primary.main' : 'transparent',
                      border: 'none',
                      borderRadius: 999,
                      color: on ? 'common.white' : 'text.secondary',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      fontFamily: 'inherit',
                      fontSize: 13,
                      fontWeight: 700,
                      gap: 0.75,
                      height: 32,
                      px: 1.625,
                      transition: 'all .14s ease',
                    }}
                  >
                    {icon}
                    {label}
                  </Box>
                );
              })}
            </Box>

            <Box
              component={'button'}
              onClick={() => verbsView.setHideKnown(!verbsView.hideKnown)}
              sx={{
                alignItems: 'center',
                bgcolor: verbsView.hideKnown
                  ? (t) =>
                      t.palette.mode === 'light'
                        ? 'rgba(59,44,147,0.10)'
                        : 'rgba(155,124,255,0.18)'
                  : 'background.paper',
                border: '1px solid',
                borderColor: verbsView.hideKnown ? 'primary.main' : 'primary.tableBorder',
                borderRadius: 999,
                color: verbsView.hideKnown ? 'primary.main' : 'text.secondary',
                cursor: 'pointer',
                display: 'inline-flex',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 700,
                gap: 0.875,
                height: 34,
                px: 1.5,
                transition: 'all .14s ease',
              }}
            >
              {verbsView.hideKnown ? (
                <VisibilityOffIcon sx={{ fontSize: 18 }} />
              ) : (
                <VisibilityIcon sx={{ fontSize: 18 }} />
              )}
              {'Сховати відомі'}
            </Box>

            <Tooltip
              title={
                verbsView.viewMode === 'table'
                  ? 'Налаштування таблиці'
                  : 'Налаштування карток'
              }
              placement={'top'}
            >
              <IconButton
                size={'small'}
                onClick={(e) => setSettingsAnchor(e.currentTarget)}
                sx={(t) => ({
                  bgcolor: settingsAnchor
                    ? t.palette.mode === 'light'
                      ? 'rgba(59,44,147,0.10)'
                      : 'rgba(155,124,255,0.18)'
                    : 'background.paper',
                  border: '1px solid',
                  borderColor: settingsAnchor ? 'primary.main' : 'primary.tableBorder',
                  borderRadius: 1.25,
                  color: settingsAnchor ? 'primary.main' : 'text.secondary',
                  height: 38,
                  transition: 'all .14s ease',
                  width: 38,
                })}
              >
                <TuneIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Stack>

          <VocabularyVerbsView
            view={verbsView}
            onSetStatus={noopSetStatus}
            onDeleteOne={noopDeleteOne}
          />

          <Stack sx={{ minHeight: 40 }} />
        </UIContainer>
      </WithLoader>

      <Dialog
        disableScrollLock
        open={addDialogOpen}
        onClose={closeAddDialog}
        fullWidth
        maxWidth={'xs'}
      >
        <DialogTitle
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          {`Виберіть стан для ${verbsView.selectedIds.length} обраних дієслів:`}
          <IconButton onClick={closeAddDialog} size={'small'}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {verbAddStatusOptions.map(({ label, value: v }) => (
            <Box
              key={v}
              onClick={() => setAddStatus(v)}
              sx={{
                alignItems: 'center',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                py: 1,
              }}
            >
              {label}
              <Checkbox
                checked={addStatus === v}
                disableTouchRipple
                onChange={() => setAddStatus(v)}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog}>{'Скасувати'}</Button>
          <Button
            loading={adding}
            onClick={addVerbsToVocab}
            variant={'contained'}
          >
            {'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      <TableSettingsDialog
        anchorEl={settingsAnchor}
        settings={tableSettings}
        onClose={() => setSettingsAnchor(null)}
        onReset={resetTableSettings}
        onSave={saveTableSettings}
      />
    </>
  );
};

export default IrregularVerbCategoryPage;
